<template>
  <div
    ref="card"
    class="
      tw-isolate
      tw-rounded-lg
      tw-border
      tw-border-solid
      tw-border-sf-slate-lighter-1
      tw-bg-white
    "
  >
    <div
      class="
        tw-flex
        tw-items-center
        tw-justify-between
        tw-border-0
        tw-border-b
        tw-border-solid
        tw-border-sf-slate-lighter-1
        tw-px-6
        tw-py-4
      "
      data-testid="users-list-toolbar"
    >
      <UsersToolbar />
      <SfButtonDs
        variant="primary"
        icon-before="plus"
        data-testid="users-list-add"
        @click="userPanel.openNew(list === userLists.EXTERNAL)"
      >
        {{ I18n.t(ADD_BUTTON_KEYS[list]) }}
      </SfButtonDs>
    </div>
    <!-- This wrapper rounds the bottom corners of the table. It cannot move up
         to the card, which would cut off the filter menus that open past the
         toolbar. -->
    <div class="tw-overflow-hidden tw-rounded-b-lg">
      <div
        ref="scrollBox"
        class="tw-overflow-y-auto"
        :style="scrollBoxStyle"
        data-testid="users-list-scroll-container"
      >
        <div
          v-if="loading"
          class="tw-flex tw-flex-col tw-gap-4 tw-px-4 tw-py-6"
          data-testid="users-list-loading"
        >
          <SfSkeletonLoader
            v-for="placeholder in SKELETON_ROWS"
            :key="placeholder"
            width="100%"
            height="1.5rem"
          />
        </div>
        <div
          v-else-if="error"
          class="tw-px-4 tw-py-6 tw-text-sm tw-text-sf-slate-dark-1"
          data-testid="users-list-error"
        >
          {{ I18n.t('user.table.load_error') }}
        </div>
        <UsersNoResults v-else-if="users.length === 0 && isFiltered" />
        <EmptyState
          v-else-if="users.length === 0"
          :text="I18n.t(EMPTY_STATE_KEYS[list])"
        />
        <UsersTable
          v-else
          :list="list"
          :rows="users"
          :sort-by="sortBy"
          :sort-direction="sortDirection"
          @open="userPanel.open($event)"
          @sort="setSort"
        />
      </div>
      <div
        v-if="totalPages > 1"
        class="
          tw-border-x-0
          tw-border-b-0
          tw-border-t
          tw-border-solid
          tw-border-sf-slate-lighter-1
          tw-py-5
        "
        data-testid="users-list-pagination"
      >
        <SfPaginationDs
          :current-page="page"
          :total-pages="totalPages"
          @change-page="setPage"
        />
      </div>
    </div>

    <UserDetailPanel @refresh="usersList.loadUsers()" />
  </div>
</template>

<script setup lang="ts">
import type { UserList } from '@app/application/user-management/types'

import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { storeToRefs } from 'pinia'
import { useEventListener, useThrottleFn } from '@vueuse/core'

import {
  ADD_BUTTON_KEYS,
  EMPTY_STATE_KEYS,
  MIN_SCROLL_BOX_HEIGHT,
  PAGE_BOTTOM_PADDING,
  SKELETON_ROWS,
} from '@app/application/user-management/constants'
import { userLists } from '@app/application/user-management/types'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'
import { useUsersListStore } from '@app/application/user-management/stores/users-list'
import useI18n from '@app/shared/vue/composables/use-I18n'
import EmptyState from '@app/shared/vue/components/molecules/empty-state.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfPaginationDs from '@app/shared/vue/components/sf-pagination/sf-pagination-ds.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import UserDetailPanel from './user-panel/user-detail-panel.vue'
import UsersNoResults from './list/users-no-results.vue'
import UsersToolbar from './list/users-toolbar.vue'
import UsersTable from './users-table.vue'

defineOptions({ name: 'UsersList' })

interface Props {
  list: UserList
}

const { list } = defineProps<Props>()

const I18n = useI18n()
const usersList = useUsersListStore()
const userPanel = useUserPanelStore()

const {
  users,
  totalPages,
  loading,
  error,
  sortBy,
  sortDirection,
  page,
  isFiltered,
} = storeToRefs(usersList)
const { setSort, setPage } = usersList

usersList.initialize(list)

const card = ref<HTMLElement | null>(null)
const scrollBox = ref<HTMLElement | null>(null)
const scrollBoxHeight = ref('')

// The height is locked (not just capped) on multi-page lists so the pagination
// footer keeps its place on a short last page and while skeletons load. It never
// drops below MIN_SCROLL_BOX_HEIGHT: on a window too short for that, the card
// overflows and the page scrolls, which keeps a few rows reachable instead of
// collapsing the table to nothing.
const scrollBoxStyle = computed(() => {
  if (!scrollBoxHeight.value) return undefined

  return totalPages.value > 1
    ? { height: scrollBoxHeight.value }
    : { maxHeight: scrollBoxHeight.value }
})

// Distance from the top of the document to the card, kept fixed as the page
// scrolls. getBoundingClientRect().top alone shrinks while scrolling, which on a
// short window (where the page itself scrolls) would grow the box on the next
// remeasure and push the pagination out of view again. Adding back the scroll of
// every ancestor (the window in the old layout shell, an inner container in the
// new one) cancels that.
const cardTopOffset = (element: HTMLElement): number => {
  let scrolled = window.scrollY
  let parent = element.parentElement

  while (parent) {
    scrolled += parent.scrollTop
    parent = parent.parentElement
  }

  return element.getBoundingClientRect().top + scrolled
}

const updateScrollBoxHeight = () => {
  if (!card.value || !scrollBox.value) return

  const cardChrome = card.value.offsetHeight - scrollBox.value.offsetHeight
  const available =
    window.innerHeight -
    cardTopOffset(card.value) -
    cardChrome -
    PAGE_BOTTOM_PADDING

  scrollBoxHeight.value = `${Math.max(
    Math.floor(available),
    MIN_SCROLL_BOX_HEIGHT
  )}px`
}

onMounted(updateScrollBoxHeight)

// trailing: true, so the height settles on the final size of a resize drag.
useEventListener(
  window,
  'resize',
  useThrottleFn(updateScrollBoxHeight, 100, true)
)

watch([loading, totalPages], () => nextTick(updateScrollBoxHeight))
</script>
